import { useMemo } from 'react';

import {
  FetchSelectedLifestyleGqlQuery,
  SelectedLifestyleUUID
} from '../../selectedLifestylesTypes';

import { FetchSelectedLifestyleCacheKey } from '../../selectedLifestylesTypes';

import { useShowQuery } from '../../../common/reactQuery/hooks/useShowQuery';

interface SelectedLifestyleResponse<ItemType> {
  selectedLifestyle: ItemType | null;
}

interface SelectedLifestyleOptions<ItemType> {
  selectedLifestyles?: ItemType[];
  cacheKey: FetchSelectedLifestyleCacheKey;
  query: FetchSelectedLifestyleGqlQuery;
  uuid: SelectedLifestyleUUID;
  options?: {
    cacheTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
}

interface UseSelectedLifestyleItem {
  uuid: SelectedLifestyleUUID;
}

const itemKey = 'selectedLifestyle';

function useSelectedLifestyle<ItemType extends UseSelectedLifestyleItem>({
  selectedLifestyles,
  cacheKey,
  query,
  uuid,
  options = {}
}: SelectedLifestyleOptions<ItemType>) {
  const placeholderData = useMemo<SelectedLifestyleResponse<ItemType>>(() => {
    const selectedLifestyle = selectedLifestyles?.find(
      (selectedLifestyle) => selectedLifestyle.uuid === uuid
    );

    return selectedLifestyle ? { selectedLifestyle } : null;
  }, [selectedLifestyles, uuid]);

  const {
    item,
    itemError,
    itemErrorData,
    itemFetched,
    itemFetching,
    itemLoading,
    itemIsPlaceholderData,
    refetchItem,
    updateItemCache
  } = useShowQuery<SelectedLifestyleResponse<ItemType>, ItemType>({
    cacheKey,
    itemKey,
    options,
    placeholderData,
    query,
    uuid
  });

  return {
    selectedLifestyle: item,
    selectedLifestyleError: itemError,
    selectedLifestyleErrorData: itemErrorData,
    selectedLifestyleFetched: itemFetched,
    selectedLifestyleFetching: itemFetching,
    selectedLifestyleIsPlaceholderData: itemIsPlaceholderData,
    selectedLifestyleLoading: itemLoading,
    selectedLifestyleUpdateCache: updateItemCache,
    refetchSelectedLifestyle: refetchItem
  };
}

export default useSelectedLifestyle;
